import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import isEqual from "date-fns/isEqual";
import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import { Dialog } from "@mui/material";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@common-ui/ui";
import CloseIcon from "@mui/icons-material/Close";
import "react-datepicker/dist/react-datepicker.css";

import { CreateWorkEvent } from "./CreateWorkEvent";
import { removeNullAndUndefinedFromObject } from "../../utils";
import { createWorkEvent } from "../../services";

export const CreateWorkEventDialog = ({
  open,
  handleClose,
  currentOrgId,
  customer,
  places,
  setOpenCustomerPlaceDialog,
}) => {
  let history = useHistory();

  const formik = useFormik({
    // enableReinitialize: true,
    initialValues: {
      work_order_reference: "",
      purchase_order_reference: "",
      scheduled_start_date: null,
      scheduled_end_date: null,
      notes: "",
    },
    validationSchema: Yup.object({
      work_order_reference: Yup.string().trim(),
      purchase_order_reference: Yup.string().trim(),
      scheduled_start_date: Yup.date().nullable(),
      scheduled_end_date: Yup.date().nullable(),
      notes: Yup.string().trim(),
    }),
    onSubmit: () => {
      if (!customer) {
        return alert("Please select Owner or Client!");
      }
      let attributes = {
        work_order_reference: formik.values.work_order_reference
          ? formik.values.work_order_reference
          : null,
        purchase_order_reference: formik.values.purchase_order_reference
          ? formik.values.purchase_order_reference
          : null,
        scheduled_start_date: formik.values.scheduled_start_date
          ? formik.values.scheduled_start_date
          : null,
        scheduled_end_date: formik.values.scheduled_end_date
          ? formik.values.scheduled_end_date
          : null,
        notes: formik.values.notes ? formik.values.notes : null,
        status: "Draft",
      };

      attributes = removeNullAndUndefinedFromObject(attributes);
      attributes = {
        ...attributes,
        for_org: !customer?.system_id ? customer?.id : null,
        for_customer: customer?.system_id ? customer?.system_id : null,
        for_place: places?.system_id ? places?.system_id : [],
      };

      const handleCreateWorkEvent = async () => {
        try {
          const response = await createWorkEvent(currentOrgId, attributes);
          if (response?.status === 200) {
            history.push(
              `/work-events/${currentOrgId}/work-event/${response?.data?.id}`
            );
          }
        } catch (error) {
          alert(error);
        }
      };

      handleCreateWorkEvent();
    },
  });

  useEffect(() => {
    if (formik.values.scheduled_end_date === null) {
      formik.setFieldValue(
        "scheduled_end_date",
        formik.values.scheduled_start_date
      );
    } else {
      if (
        isEqual(
          new Date(formik.values.scheduled_start_date),
          new Date(formik.values.scheduled_end_date)
        ) ||
        isAfter(
          new Date(formik.values.scheduled_start_date),
          new Date(formik.values.scheduled_end_date)
        )
      ) {
        formik.setFieldValue(
          "scheduled_end_date",
          formik.values.scheduled_start_date
        );
      } else {
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.scheduled_start_date]);

  useEffect(() => {
    if (
      formik.values.scheduled_end_date &&
      isBefore(
        new Date(formik.values.scheduled_end_date),
        new Date(formik.values.scheduled_start_date)
      )
    ) {
      formik.setFieldValue(
        "scheduled_start_date",
        formik.values.scheduled_end_date
      );
    } else {
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.scheduled_end_date]);

  const handleDialogClose = (_event, reason) => {
    if (reason && reason === "backdropClick") return;
    formik.resetForm();
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      aria-labelledby="create-work-event"
      aria-describedby="Allow user to creare work event"
      scroll="paper"
      fullWidth={true}
      maxWidth="xlg"
      disableEscapeKeyDown={true}
    >
      <DialogTitle sx={{ px: 2 }} id="alert-dialog-title" scroll="paper">
        <Stack sx={{ alignItems: "left" }}>
          <Typography fontSize={18} variant="h4">
            Complete Work Event Information
          </Typography>
        </Stack>
        {open ? (
          <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 2 }}>
        <CreateWorkEvent
          formik={formik}
          customer={customer}
          places={places}
          setOpenCustomerPlaceDialog={setOpenCustomerPlaceDialog}
        />
      </DialogContent>

      <DialogActions>
        <div>
          <Button
            color="primary"
            fullWidth
            size="large"
            onClick={handleDialogClose}
            variant="text"
          >
            Cancel
          </Button>
        </div>
        <div>
          <Button
            color="primary"
            disabled={!customer}
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            onClick={formik.handleSubmit}
          >
            Save
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};
